/**
 * サブタスク「●●送付」から出力画面（AkbsOutput）を別タブで開く
 * 清掃員コード・ON にする一式・退職日・雇用契約書の要否はバック（akbsOutput/taskLink）が雇い入れメインタスクと申請から決める
 * サブタスク画面（TaskRequest）・進み表（TaskStatusTracker）・雇用指示入力画面のサブタスクパネル（AkbsSubTaskPanel）の 3 か所で同じものを使う
 */
import * as backend from '@libs/backend'
import * as dialogs from '@libs/dialogs'
import { PIGEON_CONFIG } from '@/config'

/**
 * AKBS の「欠員募集」画面を開くボタンを出すサブタスクか（名前に「求人確認」を含む）
 * @param {Object} task サブタスク
 */
export function isRecruitTask(task) {
  return !!task && String(task.requestTask || '').includes('求人確認')
}

/**
 * AKBS の「欠員募集」画面を別タブで開く（AKBS 側のログインは利用者が行う）
 */
export function openAkbsRecruit() {
  window.open(PIGEON_CONFIG.akbsUrl + 'recruit', '_blank')
}

/**
 * 出力画面を開くボタンを出すサブタスクか（名前に「送付」を含む）
 * メインタスクが雇い入れ（清掃員）かは呼ぶ側が見る（進み表はメインタスクのカテゴリーがそろっていないことがあるので名前だけで出し、開くときにバックが確かめる）
 * @param {Object} task サブタスク
 */
export function isSendTask(task) {
  return !!task && String(task.requestTask || '').includes('送付')
}

/**
 * 出力画面を「入社用具一式」ON で開くサブタスクか（「●●送付」のうち名前に「備品」を含む。備品送付など）
 * 申請の種別で決める一式（入社書類一式など）ではなく、用具の書類だけを出す
 * @param {Object} task サブタスク
 */
export function isToolTask(task) {
  return isSendTask(task) && String(task.requestTask || '').includes('備品')
}

/**
 * 出力画面の「給与奉行」タブを開くボタンを出すサブタスクか（名前に「給与奉行」を含む）
 * メインタスクが雇い入れ（清掃員）かは呼ぶ側が見る
 * @param {Object} task サブタスク
 */
export function isBugyoTask(task) {
  return !!task && String(task.requestTask || '').includes('給与奉行')
}

/**
 * 出力画面の「給与奉行」タブを別タブで開く（人の検索はしない。「未出力の清掃員のみ」が ON の初期状態）
 * @param {*} context 画面（this。$router を使う）
 */
export function openAkbsOutputBugyo(context) {
  window.open(context.$router.resolve({ name: 'AkbsOutput', query: { tab: 'bugyo' } }).href, '_blank')
}

/**
 * 出力画面の「契約書返送」タブを開くボタンを出すサブタスクか（名前に「返送」と「確認」を含む）
 * @param {Object} task サブタスク
 */
export function isContractReturnTask(task) {
  const name = !!task && String(task.requestTask || '')
  return !!name && name.includes('返送') && name.includes('確認')
}

// 清掃員コードがまだ無いときの知らせ（新規入社・再入社は AKBS に登録したときに採番される。バックの akbsOutput/taskLink の理由と同じ文言）
const NO_STAFF_CODE_TEXT = 'AKBS に登録すると開けます（新規入社・再入社は登録したときに清掃員コードが決まります）。'

/**
 * 出力画面の「契約書返送」タブを別タブで開く（検索欄にその清掃員コードが入り、検索した状態）
 * 清掃員コードがまだ無ければ開かずに知らせる（出力画面へのリンクと同じ）
 * @param {*} context 画面（this。$router を使う）
 * @param {String} staffCode 清掃員コード
 * @returns {Boolean} 開いたら true
 */
export async function openAkbsContractReturn(context, staffCode) {
  if (!staffCode) {
    await dialogs.showErrorDialog('契約書返送を開けません', NO_STAFF_CODE_TEXT)
    return false
  }
  window.open(context.$router.resolve({ name: 'AkbsOutput', query: { tab: 'contractReturn', staffCode: String(staffCode) } }).href, '_blank')
  return true
}

/**
 * 登録済み一覧の「書類」タブを開くボタンを出すサブタスクか（名前に「返送」と「保管」を含む）
 * @param {Object} task サブタスク
 */
export function isDocKeepTask(task) {
  const name = !!task && String(task.requestTask || '')
  return !!name && name.includes('返送') && name.includes('保管')
}

/**
 * 登録済み一覧を別タブで開く（その清掃員コードで検索して選んだ状態。「清掃員」の軸の指定のタブ）
 * 清掃員コードがまだ無ければ開かずに知らせる（出力画面へのリンクと同じ）
 * @param {*} context 画面（this。$router を使う）
 * @param {String} staffCode 清掃員コード
 * @param {String} tab 清掃員の中のタブ（AkbsRegisteredList の STAFF_TABS の key。docs・emergency など）
 * @returns {Boolean} 開いたら true
 */
async function openAkbsRegisteredTab(context, staffCode, tab) {
  if (!staffCode) {
    await dialogs.showErrorDialog('登録済み一覧を開けません', NO_STAFF_CODE_TEXT)
    return false
  }
  window.open(context.$router.resolve({ name: 'AkbsRegisteredList', query: { tab, staffCode: String(staffCode) } }).href, '_blank')
  return true
}

/**
 * 登録済み一覧の「書類」タブを別タブで開く（「●●返送保管」のサブタスクから）
 */
export function openAkbsRegisteredDocs(context, staffCode) {
  return openAkbsRegisteredTab(context, staffCode, 'docs')
}

/**
 * 登録済み一覧の「緊急連絡先」タブを開くボタンを出すサブタスクか（名前に「緊急連絡先」を含む。「交通費・緊急連絡先登録」）
 * @param {Object} task サブタスク
 */
export function isEmergencyTask(task) {
  return !!task && String(task.requestTask || '').includes('緊急連絡先')
}

/**
 * 登録済み一覧の「緊急連絡先」タブを別タブで開く（「交通費・緊急連絡先登録」のサブタスクから）
 */
export function openAkbsRegisteredEmergency(context, staffCode) {
  return openAkbsRegisteredTab(context, staffCode, 'emergency')
}

/**
 * 出力画面を別タブで開く。開けないときは理由を知らせる
 * @param {*} context 画面（this。$router を使う）
 * @param {String} mainId 雇い入れメインタスクの _id
 * @param {Object} task 押したサブタスク（名前に「備品」を含めば一式は申請の種別に関わらず「入社用具一式」。省かれたら種別で決める）
 * @returns {Boolean} 開いたら true
 * API: akbsOutput/taskLink
 */
export async function openAkbsOutputFromTask(context, mainId, task) {
  if (!mainId) {
    await dialogs.showErrorDialog('出力画面', 'メインタスクが分からないので開けません。')
    return false
  }
  let link = null
  try {
    const res = await backend.postData('akbsOutput/taskLink', { mainId: String(mainId), tool: isToolTask(task) })
    link = res.data && res.data.data
  } catch (e) {
    console.log(e)
  }
  if (!link) {
    await dialogs.showErrorDialog('出力画面', '出力画面を開く情報を取れませんでした。もう一度お試しください。')
    return false
  }
  if (!link.ok) {
    await dialogs.showErrorDialog('出力画面を開けません', link.reason || '')
    return false
  }
  const query = { staffCode: link.staffCode }
  if (link.set) {
    query.set = link.set
  }
  // 一式ではなく書類を直接 ON にするとき（金額のみ変更：雇用契約書と雇用契約締結案内だけ）
  if (Array.isArray(link.docs) && link.docs.length) {
    query.docs = link.docs.join(',')
  }
  if (link.retireDate) {
    query.retireDate = link.retireDate
  }
  if (link.contract === false) {
    query.contract = '0'
  }
  // 契約書を出すとき（入社・契約変更の一式、または契約書を直接 ON）だけ、契約期間の開始日の初期値（終了日などは画面が自動で入れる）
  if (link.contractFrom && (['hire', 'change'].includes(link.set) || (Array.isArray(link.docs) && link.docs.includes('contract')))) {
    query.contractFrom = link.contractFrom
  }
  if (link.notice) {
    query.notice = link.notice
  }
  // チェックを付ける物件・契約コード（物件追加・契約内容変更・金額のみ変更。雇用指示画面でした操作でバックが決める）
  // 1 つも無いときも空で渡す（渡さないと出力画面の決まりで付いてしまう）
  if (Array.isArray(link.emps)) {
    query.emps = link.emps.join(',')
  }
  window.open(context.$router.resolve({ name: 'AkbsOutput', query }).href, '_blank')
  return true
}
